import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import {
  MONEY_NOTE_MAX,
  MONEY_PURPOSES,
  formatPence,
  transferFee,
  type ChatMessage,
  type ChatMoney,
  type MoneyAllowanceReply,
  type MoneyPurpose,
  govPence,
} from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { api } from '../lib/api';
import { useOverlay } from '../lib/back-stack';
import { useCatalogue } from '../lib/content';
import { useGovEffects } from '../lib/gov';
import { actionKey } from '../lib/format';

// Money in a direct chat, as in Lagos Life ("💸 Send money"): a card for money sent or asked for, and the sheet
// that sends or asks. The server decides everything (the fee, the limits, the wait for a new life, whether a
// large transfer waits for a check); the sheet only shows what it will do.

const QUICK = [100, 500, 1_000, 2_000, 5_000, 10_000];
const PURPOSES = Object.entries(MONEY_PURPOSES) as [MoneyPurpose, { label: string; icon: string }][];
// "20", "20.5" or "£1,250.00" in pounds → whole pence (null when it is not an amount)
export function poundsToPence(text: string): number | null {
  const clean = text.replace(/[£,\s]/g, '');
  if (!/^\d{1,9}(\.\d{1,2})?$/.test(clean)) return null;
  const pence = Math.round(Number(clean) * 100);
  return pence > 0 ? pence : null;
}
const purposeText = (purpose: MoneyPurpose) => `${MONEY_PURPOSES[purpose].icon} ${MONEY_PURPOSES[purpose].label}`;
const dayOf = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

// the card in the chat
export function MoneyCard({
  message,
  otherName,
  busy,
  onAnswer,
}: {
  message: ChatMessage;
  otherName: string;
  busy: boolean;
  onAnswer?: (requestId: string, answer: 'pay' | 'decline' | 'cancel') => void;
}) {
  const money = message.money as ChatMoney;
  const status =
    money.type === 'sent'
      ? money.status === 'held'
        ? 'Waiting for a quick check by the team'
        : money.status === 'returned'
          ? message.mine
            ? 'Came back to you after a check'
            : 'Returned to the sender after a check'
          : message.mine
            ? `Sent to ${otherName}`
            : 'Sent to you'
      : money.status === 'open'
        ? message.mine
          ? `Waiting for ${otherName}`
          : 'Asked of you'
        : money.status === 'paid'
          ? 'Paid ✓'
          : money.status === 'declined'
            ? 'Declined'
            : 'Cancelled';
  return (
    <div className="money-card" data-type={money.type} data-status={money.status}>
      <div className="money-card-top">
        <span className="money-card-icon" aria-hidden="true">
          {money.type === 'sent' ? '💸' : '🙏'}
        </span>
        <div>
          <small>
            {money.type === 'sent'
              ? message.mine
                ? 'You sent'
                : 'You got'
              : message.mine
                ? 'You asked for'
                : `${message.sender.firstName} asks for`}
          </small>
          <strong>{formatPence(money.amountPence)}</strong>
        </div>
      </div>
      <p className="money-card-what">
        {purposeText(money.purpose)}
        {money.note && <> · {money.note}</>}
      </p>
      <p className="money-card-status">{status}</p>
      {money.type === 'request' && money.status === 'open' && onAnswer && (
        <div className="money-card-actions">
          {message.mine ? (
            <button
              type="button"
              className="house-button quiet"
              disabled={busy}
              onClick={() => onAnswer(money.id, 'cancel')}
            >
              Cancel request
            </button>
          ) : (
            <>
              <button
                type="button"
                className="house-button quiet"
                disabled={busy}
                onClick={() => onAnswer(money.id, 'decline')}
              >
                Decline
              </button>
              <button
                type="button"
                className="house-button save"
                disabled={busy}
                onClick={() => onAnswer(money.id, 'pay')}
              >
                Pay {formatPence(money.amountPence)}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// the sheet: send money, ask for it, or buy them food (a meal from the menu, delivered at once)
export function MoneySheet({
  lifeId,
  otherName,
  onClose,
  onDone,
}: {
  lifeId: string;
  otherName: string;
  onClose: () => void;
  onDone: (message: ChatMessage, walletPence: number) => void;
}) {
  const [allowance, setAllowance] = useState<MoneyAllowanceReply | null>(null);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const [mode, setMode] = useState<'send' | 'ask' | 'food'>('send');
  const [mealId, setMealId] = useState<string | null>(null);
  // food support (the Mayor's policy): the menu costs less, as the server charges it
  const gov = useGovEffects();
  const menu = (useCatalogue()?.meals ?? [])
    .filter((item) => item.visible)
    .map((item) => ({ ...item, pricePence: govPence(item.pricePence, gov.food) }));
  const meal = menu.find((item) => item.id === mealId) ?? null;
  const [amount, setAmount] = useState('');
  const [purpose, setPurpose] = useState<MoneyPurpose>('gift');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  // one key per attempt, kept if it is tried again, so a slow network never sends twice
  const key = useRef<string | null>(null);
  useEffect(() => {
    let alive = true;
    api.money
      .allowance(lifeId)
      .then((reply) => alive && setAllowance(reply))
      .catch((error: unknown) => alive && setFailure(describeError(error)));
    return () => {
      alive = false;
    };
  }, [lifeId]);
  // back or Escape closes the sheet, not the chat behind it
  useOverlay(true, onClose);

  const pence = poundsToPence(amount);
  const rules = allowance && { transferFeeBps: allowance.feeBps, transferFeeMinPence: allowance.feeMinPence };
  const fee = pence && rules ? transferFee(pence, rules) : 0;
  const leftToday = allowance ? Math.max(0, allowance.dailyLimitPence - allowance.sentTodayPence) : 0;
  // the most that can be sent now: the wallet with the fee taken off, one transfer's limit, what is left today
  const most = (() => {
    if (!allowance || !rules) return 0;
    // start from the wallet less the fee's share, then step down past rounding and the minimum fee
    let top = Math.min(
      allowance.perTransferPence,
      leftToday,
      Math.floor(allowance.walletPence / (1 + rules.transferFeeBps / 10_000)),
    );
    while (top > 0 && top + transferFee(top, rules) > allowance.walletPence) top -= 1;
    return Math.max(0, top);
  })();
  // a number typed into the note is probably meant as the amount (Lagos Life catches this too)
  const noteAmount = mode === 'send' && /^[£\s,.\d]+$/.test(note.trim()) ? poundsToPence(note) : null;
  const waiting = mode === 'send' && allowance?.sendFrom ? allowance.sendFrom : null;
  const problem = !allowance
    ? null
    : waiting
      ? `New lives can send money from ${dayOf(waiting)}.`
      : mode === 'ask' && !allowance.canAsk
        ? `Chat with ${otherName} first. You can ask for money once you have both written.`
        : !pence
          ? null
          : pence > allowance.perTransferPence
            ? `The most at once is ${formatPence(allowance.perTransferPence)}.`
            : mode === 'send' && pence > leftToday
              ? leftToday
                ? `You can send ${formatPence(leftToday)} more today.`
                : 'You have sent all you can today.'
              : mode === 'send' && pence + fee > allowance.walletPence
                ? `You need ${formatPence(pence + fee)} with the fee.`
                : null;

  const mealProblem =
    meal && allowance && meal.pricePence > allowance.walletPence
      ? `You need ${formatPence(meal.pricePence)} for that.`
      : null;
  const buy = async () => {
    if (!meal || mealProblem || busy) return;
    setBusy(true);
    setFailure(null);
    key.current ??= actionKey('meal');
    try {
      const reply = await api.money.meal(
        { lifeId, mealId: meal.id, ...(note.trim() ? { note: note.trim() } : {}) },
        key.current,
      );
      key.current = null;
      onDone(reply.message, reply.walletPence);
    } catch (error) {
      setFailure(describeError(error));
      key.current = null;
    } finally {
      setBusy(false);
    }
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (mode === 'food') return buy();
    if (!pence || problem || busy) return;
    setBusy(true);
    setFailure(null);
    key.current ??= actionKey('money');
    const body = { lifeId, amountPence: pence, purpose, ...(note.trim() ? { note: note.trim() } : {}) };
    try {
      const reply = mode === 'send' ? await api.money.send(body, key.current) : await api.money.ask(body, key.current);
      key.current = null;
      onDone(reply.message, reply.walletPence);
    } catch (error) {
      setFailure(describeError(error));
      // a refusal will not change by trying again with the same key; a fresh try gets a fresh key
      key.current = null;
    } finally {
      setBusy(false);
    }
  };

  // drawn on the page itself, over the game's menu, wherever the chat sits
  return createPortal(
    <div className="money-layer" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <form
        className="money-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={
          mode === 'send'
            ? `Send money to ${otherName}`
            : mode === 'ask'
              ? `Ask ${otherName} for money`
              : `Buy ${otherName} food`
        }
        onSubmit={(event) => void submit(event)}
      >
        <header>
          <div className="money-tabs" role="tablist" aria-label="Send, ask or buy food">
            {(['send', 'ask', 'food'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={mode === tab}
                className={mode === tab ? 'active' : undefined}
                onClick={() => (setMode(tab), setFailure(null))}
              >
                {tab === 'send' ? '💸 Send' : tab === 'ask' ? '🙏 Ask' : '🍛 Food'}
              </button>
            ))}
          </div>
          <button type="button" className="money-close" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </header>
        {!allowance && !failure && <p className="money-muted">Loading…</p>}
        {allowance && mode === 'food' && (
          <>
            <p className="money-wallet">
              You have <b>{formatPence(allowance.walletPence)}</b> · delivered to {otherName}’s door at once
            </p>
            <div className="meal-menu" role="radiogroup" aria-label="The menu">
              {menu.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="radio"
                  aria-checked={mealId === item.id}
                  className={mealId === item.id ? 'active' : undefined}
                  onClick={() => setMealId(item.id)}
                >
                  <span aria-hidden="true">{item.icon}</span>
                  <b>{item.label}</b>
                  <small>{formatPence(item.pricePence)}</small>
                </button>
              ))}
            </div>
            <input
              className="money-note"
              aria-label="Note (optional)"
              placeholder="💬 Note (optional), e.g. get well soon"
              value={note}
              maxLength={MONEY_NOTE_MAX}
              onChange={(event) => setNote(event.target.value)}
            />
            {(mealProblem || failure) && (
              <p className="money-problem" role="alert">
                {mealProblem ?? failure?.message}
              </p>
            )}
            <button type="submit" className="house-button save money-go" disabled={!meal || !!mealProblem || busy}>
              {busy
                ? 'Ordering…'
                : meal
                  ? `Buy ${meal.label} · ${formatPence(meal.pricePence)}`
                  : 'Pick something from the menu'}
            </button>
          </>
        )}
        {allowance && mode !== 'food' && (
          <>
            <p className="money-wallet">
              You have <b>{formatPence(allowance.walletPence)}</b>
            </p>
            <label className="money-amount">
              <span aria-hidden="true">£</span>
              <input
                inputMode="decimal"
                aria-label="Amount in pounds"
                placeholder="0.00"
                value={amount}
                onChange={(event) => setAmount(event.target.value.replace(/[^\d.,£]/g, '').slice(0, 12))}
              />
              {mode === 'send' && (
                <button
                  type="button"
                  className="money-max"
                  disabled={!most}
                  onClick={() => setAmount((most / 100).toFixed(2))}
                >
                  Max
                </button>
              )}
            </label>
            <div className="money-chips" role="group" aria-label="Quick amounts">
              {QUICK.map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={pence === value}
                  className={pence === value ? 'active' : undefined}
                  onClick={() => setAmount(String(value / 100))}
                >
                  {formatPence(value).replace('.00', '')}
                </button>
              ))}
            </div>
            <div className="money-chips purposes" role="radiogroup" aria-label="What it is for">
              {PURPOSES.map(([id, item]) => (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={purpose === id}
                  className={purpose === id ? 'active' : undefined}
                  onClick={() => setPurpose(id)}
                >
                  {item.icon} {item.label}
                </button>
              ))}
            </div>
            <input
              className="money-note"
              aria-label="Note (optional)"
              placeholder={
                mode === 'send'
                  ? '💬 Note (optional), e.g. for the jollof'
                  : '💬 Note (optional), e.g. my half of the bill'
              }
              value={note}
              maxLength={MONEY_NOTE_MAX}
              onChange={(event) => setNote(event.target.value)}
            />
            {noteAmount && (
              <button
                type="button"
                className="money-hint"
                onClick={() => (setAmount((noteAmount / 100).toFixed(2)), setNote(''))}
              >
                That’s the note box 👆 Tap to send <b>{formatPence(noteAmount)}</b> instead
              </button>
            )}
            {mode === 'send' ? (
              <p className="money-muted">
                {fee ? `Fee ${formatPence(fee)} · ` : ''}Sent today {formatPence(allowance.sentTodayPence)} of{' '}
                {formatPence(allowance.dailyLimitPence)} · resets at midnight
                {pence && allowance.reviewAbovePence && pence > allowance.reviewAbovePence
                  ? ` · over ${formatPence(allowance.reviewAbovePence)} waits for a quick check by the team`
                  : ''}
              </p>
            ) : (
              <p className="money-muted">{otherName} pays it from the card in your chat, or declines.</p>
            )}
            {problem && (
              <p className="money-problem" role="alert">
                {problem}
              </p>
            )}
            {failure && (
              <p className="money-problem" role="alert">
                {failure.message}
              </p>
            )}
            <button type="submit" className="house-button save money-go" disabled={!pence || !!problem || busy}>
              {busy
                ? mode === 'send'
                  ? 'Sending…'
                  : 'Asking…'
                : !pence
                  ? 'Type an amount'
                  : mode === 'send'
                    ? `Send ${formatPence(pence)}${fee ? ` · fee ${formatPence(fee)}` : ''}`
                    : `Ask for ${formatPence(pence)}`}
            </button>
            {mode === 'send' && (
              <p className="money-safe">
                Only send money to people you know. The London Life team never asks you for money.
              </p>
            )}
          </>
        )}
        {!allowance && failure && (
          <p className="money-problem" role="alert">
            {failure.message}
          </p>
        )}
      </form>
    </div>,
    document.body,
  );
}

// a meal bought for someone, on its card in the chat
export function GiftCard({ message, otherName }: { message: ChatMessage; otherName: string }) {
  const gift = message.gift!;
  return (
    <div className="money-card gift">
      <div className="money-card-top">
        <span className="money-card-icon" aria-hidden="true">
          {gift.icon}
        </span>
        <div>
          <small>{message.mine ? `You bought ${otherName}` : `${message.sender.firstName} bought you`}</small>
          <strong className="gift-name">{gift.label}</strong>
        </div>
      </div>
      {gift.note && <p className="money-card-what">{gift.note}</p>}
      <p className="money-card-status">
        {message.mine
          ? `Delivered · ${formatPence(gift.pricePence)}`
          : 'Delivered to your door. Enjoy it while it’s hot!'}
      </p>
    </div>
  );
}
